import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { workspaceFetch } from '../../platform/business-workspaces';
import {
  createLiveProductReviewAdapter,
  CSRF_HEADER,
  SESSION_HEADER,
} from '../adapters/live-product-review-adapter';
import type {
  ClaimsInput,
  ExactReadbackView,
  ProductModelView,
  PublicationView,
  ReviewCaseView,
  RevisionRefInput,
  ServerOutcome,
} from '../adapters/product-review-port';
import {
  createDurableRequestScope,
  durableProductFacts,
  retainDurableMutation,
} from '../consumer/durable-state';
import '../ui/product-review.css';

/** Explicit local development opt-in. Public hosting and production builds cannot enable writes. */
export function synLiveEnabled(
  dev = import.meta.env.DEV,
  location = typeof window === 'undefined' ? null : window.location,
) {
  return Boolean(
    dev &&
    location &&
    ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname) &&
    new URLSearchParams(location.search).get('synLive') === '1',
  );
}

interface Session {
  readonly session: string;
  readonly csrf: string;
  readonly account: string;
  readonly expiresAt: string;
}

export function parseSynSession(value: unknown): Session {
  if (
    typeof value !== 'object' ||
    value === null ||
    Object.keys(value).sort().join(',') !== 'account,csrf,expiresAt,session'
  )
    throw new Error('Invalid session response');
  const fields = value as Record<string, unknown>;
  for (const name of ['session', 'csrf', 'account', 'expiresAt'])
    if (typeof fields[name] !== 'string' || !fields[name])
      throw new Error('Invalid session response');
  if (!Number.isFinite(Date.parse(fields.expiresAt as string)))
    throw new Error('Invalid session expiry');
  return fields as unknown as Session;
}

function outcomeText(outcome: ServerOutcome<unknown>): string {
  if (outcome.kind === 'OK') return '服务端已确认';
  if (outcome.kind === 'REFUSED')
    return `服务端拒绝 HTTP ${outcome.httpStatus} · ${outcome.code}${outcome.field ? ` · ${outcome.field}` : ''}`;
  if (outcome.kind === 'UNREACHABLE') return '结果未确认。请读回；写入可使用原请求与原幂等键重试。';
  return '响应契约异常，结果未确认。请读回或重试原请求。';
}

const emptyClaims: ClaimsInput = {
  modelName: '',
  category: '',
  manufacturerClaims: '',
  source: '',
};

const emptyReference: RevisionRefInput = {
  productId: '',
  revision: 1,
  digest: '',
  country: '',
  supplier: '',
  reviewScope: '',
};

/**
 * The LIVE consumer, held once per portal page and shared by the confirmed 06/08 sections.
 *
 * <p>WHY IT IS A CONTEXT AND NOT A SECOND PAGE. The previous revision replaced the whole portal with
 * one technical panel. Here the same typed adapter drives the portal's OWN pages: 产品管理 and its
 * detail, 我的提交, and the 08 审核中心. Every business fact on those pages is a server response —
 * a failed or unconfirmed read clears the snapshot rather than leaving stale numbers on screen.
 */
interface SynLiveState {
  readonly role: 'supplier' | 'reviewer';
  readonly origin: string;
  readonly credential: string;
  readonly setCredential: (value: string) => void;
  readonly auth: Session | null;
  readonly notice: string;
  readonly busy: boolean;
  readonly blocked: boolean;
  /**
   * Blocks READS only while a request is genuinely in flight.
   *
   * <p>WHY THIS IS NOT `blocked`, AND NOT `retry !== null` EITHER. `blocked` also covers `needsRead`,
   * which is exactly the state in which the page tells the operator to read the database back, so
   * gating reads on it disabled every way to comply. A pending retry is the same mistake one step
   * further in: the page's own instruction is "read back OR retry the original request", and reading
   * back is the cheaper of the two. New WRITES stay blocked until the outcome is confirmed; reads
   * must not be.
   */
  readonly readBlocked: boolean;
  readonly retry: { readonly key: string; readonly execute: () => Promise<void> } | null;
  readonly needsRead: boolean;
  readonly port: ReturnType<typeof createLiveProductReviewAdapter> | null;
  readonly session: string;
  readonly login: () => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly run: <T>(
    label: string,
    operation: (() => Promise<ServerOutcome<T>>) & { readonly idempotencyKey?: string },
    apply: (value: T) => void,
  ) => Promise<void>;
  readonly mutate: <T>(
    label: string,
    operation: (key: string) => Promise<ServerOutcome<T>>,
    apply: (value: T) => void,
  ) => void;
  readonly clearScope: () => void;
  readonly forgetProduct: () => void;
  readonly setNotice: (value: string) => void;
  readonly product: ProductModelView | null;
  readonly applyProduct: (value: ProductModelView) => void;
  readonly productReviews: readonly ReviewCaseView[] | null;
  readonly rememberReviews: (value: readonly ReviewCaseView[]) => void;
  readonly knownIds: readonly string[];
  readonly rememberId: (value: string) => void;
  readonly cases: readonly ReviewCaseView[];
  readonly setCases: (value: readonly ReviewCaseView[]) => void;
  readonly selected: ReviewCaseView | null;
  readonly applyCase: (value: ReviewCaseView) => void;
  readonly chooseCase: (value: ReviewCaseView) => void;
  readonly clearCase: () => void;
  readonly reference: RevisionRefInput;
  readonly setReference: (value: RevisionRefInput) => void;
  readonly caseId: string;
  readonly setCaseId: (value: string) => void;
  readonly decisionId: string;
  readonly setDecisionId: (value: string) => void;
  readonly reason: string;
  readonly setReason: (value: string) => void;
  readonly publication: PublicationView | null;
  readonly exact: ExactReadbackView | null;
  readonly setExact: (value: ExactReadbackView | null) => void;
}

const SynLiveContext = createContext<SynLiveState | null>(null);

export function SynLiveProductReviewProvider({
  role,
  children,
}: {
  readonly role: 'supplier' | 'reviewer';
  readonly children: ReactNode;
}) {
  const origin = typeof window === 'undefined' ? 'http://127.0.0.1' : window.location.origin;
  const [credential, setCredential] = useState('');
  const [auth, setAuth] = useState<Session | null>(null);
  const [notice, setNotice] = useState('请登录独立的 SYN 测试账号。刷新后重新登录并从数据库读取。');
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const scope = useRef(createDurableRequestScope());
  const [retry, setRetry] = useState<null | { key: string; execute: () => Promise<void> }>(null);
  const [productId, setProductId] = useState(() =>
    typeof window === 'undefined'
      ? ''
      : (new URLSearchParams(window.location.search).get('productId') ?? ''),
  );
  const [knownIds, setKnownIds] = useState<readonly string[]>(() =>
    typeof window === 'undefined'
      ? []
      : (new URLSearchParams(window.location.search).get('productId') ?? '')
        ? [new URLSearchParams(window.location.search).get('productId')!]
        : [],
  );
  const [country, setCountry] = useState('');
  const [supplier, setSupplier] = useState('');
  const [claims, setClaims] = useState<ClaimsInput>(emptyClaims);
  const [product, setProduct] = useState<ProductModelView | null>(null);
  const [cases, setCases] = useState<readonly ReviewCaseView[]>([]);
  const [selected, setSelected] = useState<ReviewCaseView | null>(null);
  const [reference, setReference] = useState<RevisionRefInput>(emptyReference);
  const [caseId, setCaseId] = useState('');
  const [decisionId, setDecisionId] = useState('');
  const [reason, setReason] = useState('');
  const [publication, setPublication] = useState<PublicationView | null>(null);
  const [exact, setExact] = useState<ExactReadbackView | null>(null);
  const [productReviews, setProductReviews] = useState<readonly ReviewCaseView[] | null>(null);
  const [needsRead, setNeedsRead] = useState(false);

  useEffect(() => () => scope.current.invalidate(), []);

  useEffect(() => {
    if (!auth) return;
    const timer = window.setTimeout(
      () => {
        clearScope();
        setAuth(null);
        setCredential('');
        setNotice('会话已到期，已清除本页业务快照与待重试请求。请重新登录。');
      },
      Math.max(0, Math.min(Date.parse(auth.expiresAt) - Date.now(), 2_147_483_647)),
    );
    return () => window.clearTimeout(timer);
  }, [auth]);

  function clearFacts() {
    setProduct(null);
    setCases([]);
    setSelected(null);
    setPublication(null);
    setExact(null);
    setProductReviews(null);
    setClaims(emptyClaims);
    setCountry('');
    setSupplier('');
    setReference(emptyReference);
    setCaseId('');
    setDecisionId('');
    setReason('');
  }

  function clearScope() {
    scope.current.invalidate();
    setKnownIds([]);
    lock.current = false;
    setBusy(false);
    setRetry(null);
    clearFacts();
    setNeedsRead(false);
  }

  /**
   * Drops the product snapshot without touching the request scope.
   *
   * <p>WHY THIS IS NOT `clearScope`. A mutation's apply callback runs INSIDE the request scope that
   * `run` captured, and `clearScope` invalidates that scope. Of `run`'s steps after the callback,
   * exactly one is scope-checked — the `finally` that releases the lock and the busy flag — and the
   * retry bookkeeping runs unconditionally. A callback that called `clearScope` would therefore
   * declare its own request superseded, skip that release, and then let a request the page has just
   * decided to forget write the retry state anyway. It also wipes the review and reference state,
   * which a caller that has only just consumed its own successful result has no reason to lose.
   * This drops the stale product snapshot and nothing else.
   */
  function forgetProduct() {
    setProduct(null);
    setProductReviews(null);
    setPublication(null);
    setExact(null);
  }

  async function run<T>(
    label: string,
    operation: (() => Promise<ServerOutcome<T>>) & { readonly idempotencyKey?: string },
    apply: (value: T) => void,
  ): Promise<void> {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setNotice(`${label}：等待服务端确认…`);
    const current = scope.current.capture();
    try {
      const result = await operation();
      if (!current()) return;
      setNotice(`${label}：${outcomeText(result)}`);
      if (result.kind === 'REFUSED' && result.httpStatus === 401) {
        clearScope();
        setAuth(null);
        setCredential('');
        setNotice('会话已失效，已清除本页业务快照与待重试请求。请重新登录。');
        return;
      }
      if (result.kind === 'OK') {
        setNeedsRead(false);
        if (operation.idempotencyKey) setRetry(null);
        apply(result.value);
      } else {
        clearFacts();
        setNeedsRead(true);
      }
      if (
        (result.kind === 'UNREACHABLE' || result.kind === 'CONTRACT_DEFECT') &&
        operation.idempotencyKey
      ) {
        setRetry({ key: operation.idempotencyKey, execute: () => run(label, operation, apply) });
      } else if (result.kind === 'REFUSED' && operation.idempotencyKey) setRetry(null);
    } catch {
      if (current()) {
        clearFacts();
        setNeedsRead(true);
        setNotice(`${label}：客户端异常，结果未确认。请读取数据库记录。`);
        if (operation.idempotencyKey)
          setRetry({ key: operation.idempotencyKey, execute: () => run(label, operation, apply) });
      }
    } finally {
      if (current()) {
        lock.current = false;
        setBusy(false);
      }
    }
  }

  async function login() {
    clearScope();
    setAuth(null);
    const current = scope.current.capture();
    setBusy(true);
    lock.current = true;
    try {
      // Constructor enforces the same root loopback restriction used by every business operation.
      createLiveProductReviewAdapter({ baseOrigin: origin });
      const response = await workspaceFetch(
        `${new URL(origin).origin}/management/product-review/session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential }),
          credentials: 'omit',
          redirect: 'error',
          cache: 'no-store',
        },
      );
      if (!current()) return;
      setCredential('');
      if (!response.ok) {
        setNotice(`登录被拒绝 HTTP ${response.status}`);
        return;
      }
      const session = parseSynSession(await response.json());
      if (!current()) return;
      setAuth(session);
      setNotice('已登录；请从数据库读取或新建 SYN 产品。');
    } catch {
      if (current()) setNotice('登录结果未确认，检查本地服务及连接地址。');
    } finally {
      if (current()) {
        lock.current = false;
        setBusy(false);
      }
    }
  }

  async function logout() {
    const previous = auth;
    clearScope();
    setAuth(null);
    setCredential('');
    setNotice('本页会话已清除。');
    const current = scope.current.capture();
    if (previous) {
      try {
        const response = await workspaceFetch(
          `${new URL(origin).origin}/management/product-review/session`,
          {
            method: 'DELETE',
            headers: { [SESSION_HEADER]: previous.session, [CSRF_HEADER]: previous.csrf },
            credentials: 'omit',
            redirect: 'error',
            cache: 'no-store',
          },
        );
        if (current() && !response.ok) setNotice('本页会话已清除；服务端撤销未确认。');
      } catch {
        if (current()) setNotice('本页会话已清除；服务端撤销未确认。');
      }
    }
  }

  // Memoised on the session alone: an adapter rebuilt on every render would change identity and
  // re-trigger every effect that reads through it.
  const port = useMemo(
    () =>
      auth ? createLiveProductReviewAdapter({ baseOrigin: origin, csrf: () => auth.csrf }) : null,
    [auth, origin],
  );
  const session = auth?.session ?? '';
  const blocked = busy || retry !== null || needsRead;
  const readBlocked = busy;

  function rememberId(value: string) {
    if (!value) return;
    setKnownIds((previous) => (previous.includes(value) ? previous : [value, ...previous]));
  }

  function applyProduct(value: ProductModelView) {
    setProduct(value);
    setClaims(value.claims);
    setCountry(value.country);
    setSupplier(value.supplier);
    setPublication(durableProductFacts(value).publication);
    setExact(null);
    setProductReviews(null);
    rememberId(value.productId);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('productId', value.productId);
      window.history.replaceState(null, '', url);
    }
  }

  function applyCase(value: ReviewCaseView) {
    setSelected(value);
    setReference(value.submitted);
    setCaseId(value.caseId);
    setCases((previous) => [value, ...previous.filter((item) => item.caseId !== value.caseId)]);
  }

  function chooseCase(value: ReviewCaseView) {
    const loadedCases = cases;
    clearScope();
    setCases(loadedCases);
    applyCase(value);
    setDecisionId('');
    setReason('');
    setNotice('已选择服务端审核记录；可重新读取最新版本。');
  }

  function clearCase() {
    clearScope();
    setSelected(null);
    setReference(emptyReference);
    setCaseId('');
    setNotice('已清除当前审核记录；请抄录厂商端读回的精确不可变版本受理。');
  }

  function mutate<T>(
    label: string,
    operation: (key: string) => Promise<ServerOutcome<T>>,
    apply: (value: T) => void,
  ) {
    // `port === null` means there is no session. Guarding HERE, and not only on the buttons, is what
    // makes every mutation safe: the editor dialog is rendered by a child that keeps its own open
    // state, so a session that expires while the dialog is open would otherwise leave enabled
    // controls reaching a null adapter, and the resulting TypeError would be reported as an
    // unconfirmed server result rather than as "not signed in".
    if (blocked || port === null) return;
    void run(label, retainDurableMutation(operation), apply);
  }

  const state: SynLiveState = {
    role,
    origin,
    credential,
    setCredential,
    auth,
    notice,
    busy,
    blocked,
    readBlocked,
    retry,
    needsRead,
    port,
    session,
    login,
    logout,
    run,
    mutate,
    clearScope,
    forgetProduct,
    setNotice,
    product,
    applyProduct,
    productReviews,
    rememberReviews: setProductReviews,
    knownIds,
    rememberId,
    cases,
    setCases,
    selected,
    applyCase,
    chooseCase,
    clearCase,
    reference,
    setReference,
    caseId,
    setCaseId,
    decisionId,
    setDecisionId,
    reason,
    setReason,
    publication,
    exact,
    setExact,
  };
  // `productId`, `country`, `supplier` and `claims` are written by the editor through applyProduct;
  // the editor holds the in-progress values itself, so the provider does not re-export them.
  void productId;
  void setProductId;
  void country;
  void supplier;
  void claims;
  return <SynLiveContext.Provider value={state}>{children}</SynLiveContext.Provider>;
}

export function useSynLive(): SynLiveState {
  const state = useContext(SynLiveContext);
  if (!state) throw new Error('SYN LIVE section rendered outside its provider');
  return state;
}

/** The SYN marker and the collapsed technical envelope. Engineering detail never displaces business. */
export function SynLiveSessionPanel() {
  const live = useSynLive();
  const roleLabel = live.role === 'supplier' ? '厂商' : '管理员';
  return (
    <section className="provider-syn-session" aria-label="SYN 合成测试会话">
      <p className="provider-syn-badge">
        <strong>SYN 合成测试 · 非生产</strong> · 业务结果全部来自 Java 服务与数据库 · 05
        装备写入在本机合成入口的能力与装备中心开放
      </p>
      {!live.auth ? (
        <form
          className="provider-syn-login"
          onSubmit={(event) => {
            event.preventDefault();
            void live.login();
          }}
        >
          <label>
            {roleLabel}登录凭据{' '}
            <input
              type="password"
              autoComplete="off"
              aria-label="登录凭据"
              value={live.credential}
              disabled={live.busy}
              onChange={(event) => live.setCredential(event.target.value)}
            />
          </label>{' '}
          <button className="portal-button" disabled={live.busy || !live.credential}>
            登录
          </button>
        </form>
      ) : (
        <p>
          账号：{live.auth.account} · 会话到期：{live.auth.expiresAt}{' '}
          <button className="portal-button" onClick={() => void live.logout()}>
            退出并撤销会话
          </button>
        </p>
      )}
      <p role="status" aria-live="polite">
        {live.notice}
      </p>
      {live.needsRead && (
        <p role="alert">当前状态未确认，旧业务快照已清除。成功读回或原请求重试前禁止新写入。</p>
      )}
      {live.retry && (
        <p>
          待确认请求：{live.retry.key}{' '}
          <button disabled={live.busy} onClick={() => void live.retry!.execute()}>
            原请求重试
          </button>
          （原内容与幂等键保持不变；确认前禁止新写入。）
        </p>
      )}
      <details className="provider-syn-details">
        <summary>技术请求信息</summary>
        <dl>
          <div>
            <dt>本地 Java 服务</dt>
            <dd>{live.origin}（同源）</dd>
          </div>
          <div>
            <dt>传输边界</dt>
            <dd>
              /management/product-review · 仅 127.0.0.1 · Host/Origin 校验 · 会话 + CSRF · 幂等键
            </dd>
          </div>
          <div>
            <dt>待确认请求</dt>
            <dd>{live.retry?.key ?? '无'}</dd>
          </div>
        </dl>
      </details>
    </section>
  );
}
